import { useState, useEffect, useCallback } from 'react';
import { FlaskConical, AlertTriangle, Lightbulb, Search, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────

interface DimensionScores {
  communication: number;
  problem_solving: number;
  leadership: number;
  teamwork: number;
  adaptability: number;
  initiative: number;
  technical: number;
  creativity: number;
}

interface ChemistryScore {
  id: string;
  department_id: string;
  department_name: string;
  balance_score: number;
  dimension_scores: DimensionScores;
  risk_flags: string[];
  hire_recommendations: string[];
  member_count: number;
  analyzed_at: string;
}

interface HireFit {
  candidate_id: string;
  department_id: string;
  impact_score: number;
  dimension_deltas: Record<string, number>;
  recommendation: string;
}

// ─── Helpers ─────────────────────────────────────────────────

const DIMENSIONS = ['communication', 'problem_solving', 'leadership', 'teamwork', 'adaptability', 'initiative', 'technical', 'creativity'] as const;
const DIM_LABELS: Record<string, string> = {
  communication: 'Communication',
  problem_solving: 'Problem Solving',
  leadership: 'Leadership',
  teamwork: 'Teamwork',
  adaptability: 'Adaptability',
  initiative: 'Initiative',
  technical: 'Technical',
  creativity: 'Creativity',
};

function scoreColor(score: number): string {
  if (score >= 70) return 'text-green-400';
  if (score >= 45) return 'text-amber-400';
  return 'text-red-400';
}

function scoreRingColor(score: number): string {
  if (score >= 70) return '#22c55e';
  if (score >= 45) return '#f59e0b';
  return '#ef4444';
}

// ─── SVG Ring Gauge ───────────────────────────────────────────

function RingGauge({ score, size = 72 }: { score: number; size?: number }) {
  const r = (size - 12) / 2;
  const circumference = 2 * Math.PI * r;
  const dashOffset = circumference - (score / 100) * circumference;
  const color = scoreRingColor(score);
  const cx = size / 2;
  const cy = size / 2;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#27272a" strokeWidth="8" />
      <circle
        cx={cx} cy={cy} r={r} fill="none"
        stroke={color} strokeWidth="8"
        strokeDasharray={circumference}
        strokeDashoffset={dashOffset}
        strokeLinecap="round"
        transform={`rotate(-90 ${cx} ${cy})`}
      />
      <text x={cx} y={cy + 4} textAnchor="middle" fontSize="14" fontWeight="bold" fill={color}>{score}</text>
    </svg>
  );
}

// ─── SVG Radar Chart (8-dimension polygon) ───────────────────

function RadarChart({ scores }: { scores: Record<string, number> }) {
  const cx = 120;
  const cy = 120;
  const r = 90;
  const n = DIMENSIONS.length;

  const angleStep = (2 * Math.PI) / n;
  const toPoint = (i: number, val: number) => {
    const angle = i * angleStep - Math.PI / 2;
    const dist = (val / 100) * r;
    return { x: cx + Math.cos(angle) * dist, y: cy + Math.sin(angle) * dist };
  };

  const labelPoint = (i: number) => {
    const angle = i * angleStep - Math.PI / 2;
    const dist = r + 18;
    return { x: cx + Math.cos(angle) * dist, y: cy + Math.sin(angle) * dist };
  };

  // Grid rings
  const gridRings = [25, 50, 75, 100];

  // Score polygon
  const polyPoints = DIMENSIONS.map((dim, i) => toPoint(i, scores[dim] ?? 0));
  const polyPath = polyPoints.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ') + 'Z';

  return (
    <svg viewBox="0 0 240 240" className="w-full max-w-xs mx-auto">
      {/* Grid */}
      {gridRings.map(pct => {
        const pts = DIMENSIONS.map((_, i) => toPoint(i, pct));
        const d = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ') + 'Z';
        return <path key={pct} d={d} fill="none" stroke="#3f3f46" strokeWidth="0.5" />;
      })}
      {/* Spokes */}
      {DIMENSIONS.map((_, i) => {
        const end = toPoint(i, 100);
        return <line key={i} x1={cx} y1={cy} x2={end.x} y2={end.y} stroke="#3f3f46" strokeWidth="0.5" />;
      })}
      {/* Score polygon */}
      <path d={polyPath} fill="#f59e0b22" stroke="#f59e0b" strokeWidth="2" />
      {/* Points */}
      {polyPoints.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r="3" fill="#f59e0b" />
      ))}
      {/* Labels */}
      {DIMENSIONS.map((dim, i) => {
        const lp = labelPoint(i);
        const label = DIM_LABELS[dim] ?? dim;
        const score = scores[dim] ?? 0;
        return (
          <g key={dim}>
            <text x={lp.x} y={lp.y} textAnchor="middle" fontSize="8" fill="#a1a1aa">{label}</text>
            <text x={lp.x} y={lp.y + 9} textAnchor="middle" fontSize="8" fontWeight="bold" fill={scoreRingColor(score)}>{score}</text>
          </g>
        );
      })}
    </svg>
  );
}

// ─── Department Card ──────────────────────────────────────────

function DepartmentCard({ score }: { score: ChemistryScore }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="glass rounded-xl p-4">
      <div className="flex items-center gap-4">
        <RingGauge score={score.balance_score} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground">{score.department_name}</p>
          <p className="text-xs text-muted-foreground">{score.member_count} members · analyzed {new Date(score.analyzed_at).toLocaleDateString()}</p>
          {score.risk_flags.length > 0 && (
            <div className="flex items-center gap-1 mt-1">
              <AlertTriangle className="w-3 h-3 text-red-400 shrink-0" />
              <p className="text-xs text-red-400 truncate">{score.risk_flags[0]}</p>
              {score.risk_flags.length > 1 && <span className="text-xs text-muted-foreground">+{score.risk_flags.length - 1}</span>}
            </div>
          )}
        </div>
        <button onClick={() => setExpanded(!expanded)} className="text-muted-foreground hover:text-foreground shrink-0">
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      {expanded && (
        <div className="mt-4 pt-4 border-t border-border/50 space-y-4">
          {/* Radar chart */}
          <RadarChart scores={score.dimension_scores as unknown as Record<string, number>} />

          {/* Risk flags */}
          {score.risk_flags.length > 0 && (
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 text-red-400" /> Risk Flags
              </p>
              <div className="space-y-1">
                {score.risk_flags.map((flag, i) => (
                  <p key={i} className="text-xs text-red-400 bg-red-500/10 rounded px-2 py-1">{flag}</p>
                ))}
              </div>
            </div>
          )}

          {/* Hire recommendations */}
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1.5">
              <Lightbulb className="w-3.5 h-3.5 text-amber-400" /> Hire Recommendations
            </p>
            <div className="space-y-1">
              {score.hire_recommendations.map((rec, i) => (
                <p key={i} className="text-xs text-foreground bg-amber-500/10 rounded px-2 py-1">{rec}</p>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Candidate Fit Analyzer ───────────────────────────────────

function CandidateFitAnalyzer({ departments }: { departments: ChemistryScore[] }) {
  const [candidateId, setCandidateId] = useState('');
  const [deptId, setDeptId] = useState('');
  const [result, setResult] = useState<HireFit | null>(null);
  const [loading, setLoading] = useState(false);

  async function analyze() {
    if (!candidateId.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const params = new URLSearchParams({ department_id: deptId });
      const res = await fetch(`/api/people/team-chemistry/hire-fit/${encodeURIComponent(candidateId.trim())}?${params}`);
      if (res.ok) setResult(await res.json() as HireFit);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }

  return (
    <div className="glass rounded-xl p-5 space-y-4">
      <div className="flex items-center gap-2">
        <Search className="w-4 h-4 text-amber-500" />
        <h3 className="text-sm font-semibold text-foreground">Candidate Fit Analyzer</h3>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-muted-foreground block mb-1">Candidate ID or Name</label>
          <input
            value={candidateId}
            onChange={e => setCandidateId(e.target.value)}
            placeholder="Paste candidate ID..."
            className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground"
          />
        </div>
        <div>
          <label className="text-xs text-muted-foreground block mb-1">Target Department</label>
          <select
            value={deptId}
            onChange={e => setDeptId(e.target.value)}
            className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground"
          >
            <option value="">Any department</option>
            {departments.map(d => (
              <option key={d.department_id} value={d.department_id}>{d.department_name}</option>
            ))}
          </select>
        </div>
      </div>

      <button
        onClick={() => void analyze()}
        disabled={loading || !candidateId.trim()}
        className="px-4 py-1.5 rounded-lg bg-amber-500 text-foreground hover:bg-amber-600 text-sm disabled:opacity-50"
      >
        {loading ? 'Analyzing...' : 'Analyze Team Fit'}
      </button>

      {result && (
        <div className="border border-border rounded-lg p-4 space-y-3">
          <div className="flex items-center gap-3">
            <div className={`text-2xl font-bold ${result.impact_score > 0 ? 'text-green-400' : result.impact_score < -20 ? 'text-red-400' : 'text-amber-400'}`}>
              {result.impact_score > 0 ? '+' : ''}{result.impact_score}
            </div>
            <p className="text-sm text-foreground">{result.recommendation}</p>
          </div>
          {Object.entries(result.dimension_deltas).length > 0 && (
            <div className="grid grid-cols-4 gap-2">
              {Object.entries(result.dimension_deltas).map(([dim, delta]) => (
                <div key={dim} className="text-center">
                  <p className="text-xs text-muted-foreground">{DIM_LABELS[dim] ?? dim}</p>
                  <p className={`text-xs font-semibold ${delta > 0 ? 'text-green-400' : delta < 0 ? 'text-red-400' : 'text-muted-foreground'}`}>
                    {delta > 0 ? '+' : ''}{delta}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────

export function TeamChemistryPage() {
  const [scores, setScores] = useState<ChemistryScore[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/people/team-chemistry/scores');
      if (res.ok) {
        const data = await res.json() as { scores: ChemistryScore[] };
        setScores(data.scores);
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function refresh() {
    setRefreshing(true);
    try {
      await fetch('/api/people/team-chemistry/refresh', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      await load();
    } catch { /* silent */ }
    finally { setRefreshing(false); }
  }

  const avgBalance = scores.length > 0
    ? Math.round(scores.reduce((a, s) => a + s.balance_score, 0) / scores.length)
    : 0;
  const riskyDepts = scores.filter(s => s.risk_flags.length > 0).length;
  const totalFlags = scores.reduce((a, s) => a + s.risk_flags.length, 0);

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FlaskConical className="w-5 h-5 text-amber-500" />
          <h1 className="text-xl font-bold text-foreground">WorkDNA Team Chemistry</h1>
        </div>
        <button
          onClick={() => void refresh()}
          disabled={refreshing}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground hover:bg-surface-3 disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing...' : 'Refresh All'}
        </button>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-3 gap-4">
        <div className="glass rounded-xl p-4 text-center">
          <p className="text-xs text-muted-foreground mb-1">Avg Balance Score</p>
          <p className={`text-2xl font-bold ${scoreColor(avgBalance)}`}>{avgBalance}</p>
        </div>
        <div className="glass rounded-xl p-4 text-center">
          <p className="text-xs text-muted-foreground mb-1">Departments Analyzed</p>
          <p className="text-2xl font-bold text-foreground">{scores.length}</p>
        </div>
        <div className="glass rounded-xl p-4 text-center">
          <p className="text-xs text-muted-foreground mb-1">Risk Flags</p>
          <p className={`text-2xl font-bold ${totalFlags > 0 ? 'text-red-400' : 'text-green-400'}`}>{totalFlags}</p>
          {riskyDepts > 0 && <p className="text-xs text-muted-foreground">{riskyDepts} departments</p>}
        </div>
      </div>

      {/* Department cards */}
      {loading ? (
        <div className="text-muted-foreground text-sm">Loading...</div>
      ) : scores.length === 0 ? (
        <div className="glass rounded-xl p-12 text-center">
          <FlaskConical className="w-12 h-12 text-amber-500/20 mx-auto mb-3" />
          <p className="text-foreground font-medium mb-1">No chemistry scores yet</p>
          <p className="text-sm text-muted-foreground">
            Use the Refresh button to calculate team chemistry from WorkDNA profiles.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {scores.map(score => (
            <DepartmentCard key={score.department_id} score={score} />
          ))}
        </div>
      )}

      {/* Candidate fit analyzer */}
      <CandidateFitAnalyzer departments={scores} />
    </div>
  );
}
